/**
 * Register Page Component
 * @implements TS-032 - Customer Portal Frontend Package Setup
 * @implements FS-001.2.1 - GitHub OAuth Authentication
 * @implements FS-001.1.1 - Product Owner Registration (invitation code requirement)
 * @implements BS-001.1 - Invitation Code Management
 *
 * Registration page with OAuth authentication (GitHub + Google).
 * Registers Product Owners only (operative feature deferred per FS-008.6).
 * Requires invitation code during alpha/beta phase per FS-001.1.1.
 */

import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
  TextField,
  Alert,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { GitHubOAuthButton, GoogleOAuthButton } from '@/components/auth';

export const RegisterPage = () => {
  /**
   * Invitation code state
   * @implements FS-001.1.1 - Product Owner Registration requires invitation code
   * @implements BS-001.1 - Invitation codes are single-use and validated at registration
   */
  const [invitationCode, setInvitationCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if invitation code is valid (non-empty, trimmed)
   * The actual validation happens on the backend, but we require
   * a non-empty code before allowing OAuth redirect.
   */
  const isInvitationCodeValid = invitationCode.trim().length > 0;

  /**
   * Handle OAuth button click - validate invitation code first
   */
  const handleOAuthClick = () => {
    if (!isInvitationCodeValid) {
      setError('Please enter an invitation code to continue.');
      return false;
    }
    setError(null);
    return true;
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Create Account
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 4 }}
          >
            Join Jared and start managing your projects
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Product Owners create and manage projects, define tasks, and
            collaborate with AI agents.
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Invitation Code Field
           * @implements FS-001.1.1 - During alpha/beta phase, registration requires a valid invitation code
           * @implements BS-001.1 - Invitation codes are single-use
           */}
          <TextField
            label="Invitation Code"
            value={invitationCode}
            onChange={(e) => {
              setInvitationCode(e.target.value);
              if (error) setError(null);
            }}
            fullWidth
            required
            placeholder="Enter your invitation code"
            helperText="An invitation code is required during the beta period"
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <GitHubOAuthButton
              flow="register_po"
              invitationCode={invitationCode.trim()}
              disabled={!isInvitationCodeValid}
              onAuthStart={() => handleOAuthClick()}
            />
            <GoogleOAuthButton
              flow="register_po"
              invitationCode={invitationCode.trim()}
              disabled={!isInvitationCodeValid}
              onAuthStart={() => handleOAuthClick()}
            />
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          <Typography variant="body2" color="text.secondary" align="center">
            Email/password registration will be available in a future release.
          </Typography>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Button
                component={RouterLink}
                to="/login"
                color="primary"
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
              >
                Sign in
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
